import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, Activity, Database, Users, AlertTriangle, 
  RefreshCw, CheckCircle, XCircle, Clock, Server,
  Cpu, HardDrive, MemoryStick, Loader2, TrendingUp, Pause, Play
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = ['#0A7EA4', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const HealthMonitoringPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [dbStats, setDbStats] = useState(null);
  const [errorLogs, setErrorLogs] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [timeRange, setTimeRange] = useState('24');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/health/overview`);
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to fetch health overview:', error);
    }
  }, []);

  const fetchDbStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/health/database`);
      setDbStats(response.data);
    } catch (error) {
      console.error('Failed to fetch database stats:', error);
    }
  }, []);

  const fetchErrorLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/health/errors?hours=${timeRange}`);
      setErrorLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch error logs:', error);
    }
  }, [timeRange]);

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/health/metrics?hours=${timeRange}`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    }
  }, [timeRange]);

  const fetchAllData = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setRefreshing(true);
    
    await Promise.all([
      fetchOverview(),
      fetchDbStats(),
      fetchErrorLogs(),
      fetchMetrics()
    ]);
    
    setLoading(false);
    setRefreshing(false);
  }, [fetchOverview, fetchDbStats, fetchErrorLogs, fetchMetrics]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchAllData(false);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, fetchAllData]);

  // Refetch when time range changes
  useEffect(() => {
    fetchErrorLogs();
    fetchMetrics();
  }, [timeRange, fetchErrorLogs, fetchMetrics]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'bg-green-500';
      case 'degraded': return 'bg-yellow-500';
      case 'unhealthy': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'degraded': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'unhealthy': return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#0A7EA4]" />
        </div>
      </Layout>
    );
  }

  // Prepare chart data — cap to top 15 collections so the vertical bar
  // chart stays readable (backend now returns every collection).
  const collectionData = dbStats?.collection_counts 
    ? Object.entries(dbStats.collection_counts)
        .map(([name, count]) => ({ name: name.replace(/_/g, ' '), count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 15)
    : [];

  const formatHour = (hourStr) => {
    if (!hourStr) return '';
    const hour = parseInt(hourStr.split('T')[1]?.split(':')[0] || '0');
    return `${hour}:00`;
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/tools')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin Tools
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Time range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 1 hour</SelectItem>
                <SelectItem value="6">Last 6 hours</SelectItem>
                <SelectItem value="24">Last 24 hours</SelectItem>
                <SelectItem value="168">Last 7 days</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              title={autoRefresh ? "Stop auto-refresh" : "Start auto-refresh (every 30s)"}
            >
              {autoRefresh ? (
                <>
                  <Pause className="w-4 h-4 mr-2" />
                  Auto-refresh On
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Auto-refresh
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchAllData(false)}
              disabled={refreshing}
              title="Refresh data now"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Page Title */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Activity className="w-7 h-7 text-[#0A7EA4]" />
            Health Monitoring
          </h1>
          <p className="text-gray-500 mt-1">Monitor system health, performance, and errors</p>
        </div>

        {/* Status Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Overall Status */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">System Status</p>
                  <div className="flex items-center gap-2 mt-1">
                    {getStatusIcon(overview?.status)}
                    <span className="text-lg font-semibold capitalize">{overview?.status || 'Unknown'}</span>
                  </div>
                </div>
                <div className={`w-3 h-3 rounded-full ${getStatusColor(overview?.status)}`} />
              </div>
              <p className="text-xs text-gray-400 mt-2">Uptime: {overview?.uptime || 'N/A'}</p>
            </CardContent>
          </Card>

          {/* Active Sessions */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Sessions</p>
                  <p className="text-2xl font-bold text-[#0A7EA4]">{overview?.sessions?.total_active || 0}</p>
                </div>
                <Users className="w-8 h-8 text-[#0A7EA4]/30" />
              </div>
              <div className="flex gap-3 mt-2 text-xs text-gray-500">
                <span>Admins: {overview?.sessions?.admin_sessions || 0}</span>
                <span>Users: {overview?.sessions?.user_sessions || 0}</span>
              </div>
            </CardContent>
          </Card>

          {/* Database Status */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Database</p>
                  <div className="flex items-center gap-2 mt-1">
                    {getStatusIcon(overview?.services?.database?.status)}
                    <span className="text-lg font-semibold capitalize">
                      {overview?.services?.database?.status || 'Unknown'}
                    </span>
                  </div>
                </div>
                <Database className="w-8 h-8 text-green-500/30" />
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Latency: {overview?.services?.database?.latency_ms?.toFixed(1) || 'N/A'}ms
              </p>
            </CardContent>
          </Card>

          {/* Errors */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Errors (24h)</p>
                  <p className={`text-2xl font-bold ${(overview?.errors?.last_24h || 0) > 0 ? 'text-red-500' : 'text-green-500'}`}>
                    {overview?.errors?.last_24h || 0}
                  </p>
                </div>
                <AlertTriangle className={`w-8 h-8 ${(overview?.errors?.last_24h || 0) > 0 ? 'text-red-500/30' : 'text-green-500/30'}`} />
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {(overview?.errors?.last_24h || 0) === 0 ? 'No errors detected' : 'Check error logs'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* System Resources */}
        {overview?.system && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Server className="w-5 h-5" />
                System Resources
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* CPU */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium">CPU</span>
                    </div>
                    <span className="text-sm font-bold">{overview.system.cpu_percent?.toFixed(1) || 'N/A'}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all"
                      style={{ width: `${overview.system.cpu_percent || 0}%` }}
                    />
                  </div>
                </div>

                {/* Memory */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MemoryStick className="w-4 h-4 text-purple-500" />
                      <span className="text-sm font-medium">Memory</span>
                    </div>
                    <span className="text-sm font-bold">{overview.system.memory_percent?.toFixed(1) || 'N/A'}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        overview.system.memory_percent > 80 ? 'bg-red-500' : 
                        overview.system.memory_percent > 60 ? 'bg-yellow-500' : 'bg-purple-500'
                      }`}
                      style={{ width: `${overview.system.memory_percent || 0}%` }}
                    />
                  </div>
                </div>

                {/* Disk */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <HardDrive className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium">Disk</span>
                    </div>
                    <span className="text-sm font-bold">{overview.system.disk_percent?.toFixed(1) || 'N/A'}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        overview.system.disk_percent > 80 ? 'bg-red-500' : 
                        overview.system.disk_percent > 60 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${overview.system.disk_percent || 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs for detailed views */}
        <Tabs defaultValue="metrics" className="space-y-4">
          <TabsList>
            <TabsTrigger value="metrics">
              <TrendingUp className="w-4 h-4 mr-2" />
              Metrics
            </TabsTrigger>
            <TabsTrigger value="database">
              <Database className="w-4 h-4 mr-2" />
              Database
            </TabsTrigger>
            <TabsTrigger value="errors">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Error Logs
            </TabsTrigger>
          </TabsList>

          {/* Metrics Tab */}
          <TabsContent value="metrics" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Request Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Request Activity</CardTitle>
                  <CardDescription>API requests over time</CardDescription>
                </CardHeader>
                <CardContent>
                  {metrics?.hourly_requests?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={metrics.hourly_requests}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour" tickFormatter={formatHour} fontSize={12} />
                        <YAxis fontSize={12} />
                        <Tooltip 
                          labelFormatter={(label) => `Time: ${formatHour(label)}`}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="requests" 
                          stroke="#0A7EA4" 
                          strokeWidth={2}
                          name="Requests"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="unique_users" 
                          stroke="#10B981" 
                          strokeWidth={2}
                          name="Unique Users"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-gray-400">
                      No request data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Login Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Login Activity</CardTitle>
                  <CardDescription>Successful vs failed logins</CardDescription>
                </CardHeader>
                <CardContent>
                  {metrics?.login_activity?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={metrics.login_activity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour" tickFormatter={formatHour} fontSize={12} />
                        <YAxis fontSize={12} />
                        <Tooltip 
                          labelFormatter={(label) => `Time: ${formatHour(label)}`}
                        />
                        <Legend />
                        <Bar dataKey="successful" fill="#10B981" name="Successful" />
                        <Bar dataKey="failed" fill="#EF4444" name="Failed" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-gray-400">
                      No login data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Database Tab */}
          <TabsContent value="database" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Database Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Database Statistics</CardTitle>
                  <CardDescription>Storage and index information</CardDescription>
                </CardHeader>
                <CardContent>
                  {dbStats?.stats ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-500">Collections</p>
                          <p className="text-xl font-bold">{dbStats.stats.collections}</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-500">Indexes</p>
                          <p className="text-xl font-bold">{dbStats.stats.indexes}</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-500">Data Size</p>
                          <p className="text-xl font-bold">{dbStats.stats.data_size_mb} MB</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-500">Index Size</p>
                          <p className="text-xl font-bold">{dbStats.stats.index_size_mb} MB</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-gray-400">
                      No database stats available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Collection Counts */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Collection Sizes</CardTitle>
                  <CardDescription>Document counts per collection</CardDescription>
                </CardHeader>
                <CardContent>
                  {collectionData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={collectionData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" fontSize={12} />
                        <YAxis dataKey="name" type="category" width={100} fontSize={11} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#0A7EA4">
                          {collectionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-gray-400">
                      No collection data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Errors Tab */}
          <TabsContent value="errors" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Error Summary */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg">Error Summary</CardTitle>
                  <CardDescription>Last {timeRange} hours</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-4">
                    <p className={`text-4xl font-bold ${(errorLogs?.total_errors || 0) > 0 ? 'text-red-500' : 'text-green-500'}`}>
                      {errorLogs?.total_errors || 0}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">Total Errors</p>
                  </div>
                  
                  {errorLogs?.by_type?.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-medium text-gray-700">By Type:</p>
                      {errorLogs.by_type.slice(0, 5).map((item, index) => (
                        <div key={index} className="flex justify-between items-center text-sm">
                          <span className="text-gray-600 truncate">{item.type}</span>
                          <Badge variant="outline">{item.count}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Error Log List */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Recent Errors</CardTitle>
                  <CardDescription>Latest error entries</CardDescription>
                </CardHeader>
                <CardContent>
                  {errorLogs?.errors?.length > 0 ? (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {errorLogs.errors.map((error, index) => (
                        <div key={index} className="p-3 bg-red-50 border border-red-100 rounded-lg">
                          <div className="flex justify-between items-start">
                            <Badge className="bg-red-100 text-red-700">{error.error_type || 'Error'}</Badge>
                            <span className="text-xs text-gray-500">
                              {new Date(error.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mt-2">{error.message}</p>
                          {error.endpoint && (
                            <p className="text-xs text-gray-400 mt-1">Endpoint: {error.endpoint}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-[200px] flex flex-col items-center justify-center text-gray-400">
                      <CheckCircle className="w-12 h-12 text-green-300 mb-2" />
                      <p>No errors in the selected time range</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default HealthMonitoringPage;
